import {
  faCalendar,
  faDatabase,
  faGrid,
  faGearApi,
  faLandmarkDome,
  faLayer,
  faHeadSideGear,
  faRectangleVerticalHistory,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";

import { isUserAdmin } from "../../../common/constants/userRole";
import type { Module, ModulesDTO, SubModule } from "../../../common/types/ModulesDTO";
import type { UserDTO } from "../../../common/types/UserDTO";
import type { NavigationLink, SubLink } from "../../types";
import tokenManager, { TOKEN_KEY } from "../tokenManager";
import { getFromParams } from "../url";

const constructDashboardUrl = (
  baseUrl: string,
  user: UserDTO,
  accountId?: number,
  token?: string | null,
) => {
  const { email, accountName, id } = user;
  const isAdmin = isUserAdmin(user.roles);
  const selectedVenueId = user.properties?.selectedVenueId;
  // TODO: Replace with a dedicated IS_PAID feature once it exists
  const isPaidVersion = true;

  return `${baseUrl}/dashboard?headless=true&email=${encodeURIComponent(
    email,
  )}&accountId=${accountId}&accountName=${encodeURIComponent(
    accountName || "",
  )}&userId=${id}&venueId=${selectedVenueId}&isPaidVersion=${isPaidVersion}&isAdmin=${isAdmin}&token=${token}`;
};

// baseParams carries the access token and the shell's origin, and reaches every module
// on every branch below. Venue context stays separate because only some modules take it.
const buildIframeUrl = (
  item: Module | SubModule,
  baseParams: string,
  venueContext: string,
): string => {
  if (item.isVaadin) {
    const [baseUrl, vaadinPath] = item.url.split("#!");
    return `${baseUrl}?${baseParams}#!${vaadinPath}`;
  }
  if (item.includeVenueId) {
    return `${item.url}?${venueContext}&${baseParams}`;
  }
  return `${item.url}?${baseParams}`;
};

const buildSubLinks = (
  to: string,
  baseParams: string,
  contextParams: string,
  subModules?: SubModule[],
): SubLink[] | undefined => {
  if (!subModules) return undefined;
  if (!subModules.length) return undefined;
  return subModules.map((sm) => ({
    title: sm.name,
    to: `${to}/${sm.id}`,
    iFrameUrl: buildIframeUrl(sm, baseParams, contextParams),
    path: sm.path,
    showInNav: sm.showInNav,
  }));
};

const MODULE_CONFIG: {
  key: keyof ModulesDTO;
  icon: IconDefinition;
  title: string;
  route: string;
}[] = [
  { key: "ask", icon: faHeadSideGear, title: "Ask", route: "ask" },
  { key: "dashboard", icon: faGrid, title: "Dashboard", route: "dashboard" },
  { key: "almanac", icon: faCalendar, title: "Almanac", route: "almanac" },
  { key: "platform", icon: faLayer, title: "Platform", route: "data/platform" },
  {
    key: "dataExplorer",
    icon: faDatabase,
    title: "Explorer",
    route: "data/data-explorer",
  },
  { key: "dexibitApi", icon: faGearApi, title: "Public API", route: "data/api" },
  { key: "legacy", icon: faRectangleVerticalHistory, title: "Legacy", route: "legacy" },
  {
    key: "administration",
    icon: faLandmarkDome,
    title: "Admin",
    route: "administration",
  },
];

export const constructLinks = (
  moduleData?: ModulesDTO,
  user?: UserDTO,
  accountId?: number,
  rootPath?: string,
): NavigationLink[] => {
  const token = getFromParams(TOKEN_KEY) || tokenManager.getToken();
  const selectedVenueId = user?.properties?.selectedVenueId;

  const accessTokenParam = `access_token=${token}`;
  // parentOrigin lets a cross-origin module postMessage back to the shell with an exact
  // targetOrigin instead of "*". BroadcastChannel cannot cross origins, so a module not
  // sharing our hostname has no other way to reach us. It belongs with the token rather
  // than with venue context: modules that take no venue context still need to talk back.
  const parentOriginParam = `parentOrigin=${encodeURIComponent(window.location.origin)}`;
  const baseParams = `${accessTokenParam}&${parentOriginParam}`;
  const contextParams = `headless=true&venueId=${selectedVenueId}`;

  if (!moduleData || !user) {
    return [];
  }

  return MODULE_CONFIG.reduce<NavigationLink[]>((links, { key, icon, title, route }) => {
    const module = moduleData[key];
    if (!module) return links;

    const to = `${rootPath}/${route}`;
    const iFrameUrl =
      key === "dashboard"
        ? constructDashboardUrl(module.url, user, accountId, token)
        : buildIframeUrl(module, baseParams, contextParams);

    links.push({
      to,
      title,
      fullTitle: module.name,
      icon,
      iFrameUrl,
      subLinks: buildSubLinks(to, baseParams, contextParams, module.subModules),
    });
    return links;
  }, []);
};
